import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../shared/Logo';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      {/* Header */}
      <header className="border-b border-slate-800 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size={50} />
            <span className="font-bold text-sm text-white">
              Revert<span className="text-amber-500">AI</span>
            </span>
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors">
            ← Retour
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-white mb-2">Politique de Confidentialité</h1>
        <p className="text-slate-500 text-sm mb-10">Dernière mise à jour : avril 2025</p>

        <div className="space-y-8 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-3">1. Responsable du traitement</h2>
            <p>
              RevertAI est responsable du traitement des données personnelles de ses utilisateurs
              (recruteurs, membres d'équipe). Pour les données des candidats, RevertAI agit en tant
              que sous-traitant pour le compte de l'utilisateur responsable du traitement.
            </p>
            <p className="mt-2">
              Contact :{' '}
              <a href="mailto:contact@revertai.io" className="text-amber-500 hover:underline">
                contact@revertai.io
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">2. Données collectées</h2>
            <p className="mb-2">Nous collectons les données suivantes :</p>

            <h3 className="font-medium text-slate-200 mb-1">
              Données des utilisateurs (recruteurs)
            </h3>
            <ul className="list-disc list-inside space-y-1 text-slate-400 mb-4">
              <li>Nom, prénom, adresse e-mail professionnelle</li>
              <li>Nom de l'organisation</li>
              <li>Données de connexion (horodatage, adresse IP)</li>
              <li>Données d'usage de la plateforme</li>
            </ul>

            <h3 className="font-medium text-slate-200 mb-1">
              Données des candidats (importées par les utilisateurs)
            </h3>
            <ul className="list-disc list-inside space-y-1 text-slate-400">
              <li>Nom, prénom, coordonnées (e-mail, téléphone, localisation)</li>
              <li>Informations professionnelles (CV, expériences, compétences, formations)</li>
              <li>URL LinkedIn et autres informations contenues dans les CV importés</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">3. Finalités et bases légales</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 pr-4 font-medium text-slate-200">Finalité</th>
                    <th className="text-left py-2 font-medium text-slate-200">Base légale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800 text-slate-400">
                  <tr>
                    <td className="py-2 pr-4">Fourniture et amélioration du service</td>
                    <td className="py-2">Exécution du contrat</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Gestion des comptes utilisateurs</td>
                    <td className="py-2">Exécution du contrat</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Analyse IA des CV et matching</td>
                    <td className="py-2">Intérêt légitime / contrat</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Sécurité et prévention des fraudes</td>
                    <td className="py-2">Intérêt légitime</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Respect des obligations légales</td>
                    <td className="py-2">Obligation légale</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">4. Destinataires des données</h2>
            <p>Les données sont accessibles aux personnes suivantes :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-slate-400">
              <li>L'équipe RevertAI (accès limité et contrôlé)</li>
              <li>Les membres de l'organisation de l'utilisateur sur la plateforme</li>
              <li>
                Nos sous-traitants techniques (hébergement, IA, monitoring) dans le cadre de
                contrats conformes au RGPD
              </li>
            </ul>
            <p className="mt-3">Sous-traitants principaux :</p>
            <ul className="list-disc list-inside mt-1 space-y-1 text-slate-400">
              <li>
                <span className="text-slate-300">Supabase</span> — Base de données et
                authentification (hébergement EU disponible)
              </li>
              <li>
                <span className="text-slate-300">Vercel</span> — Hébergement de l'application
              </li>
              <li>
                <span className="text-slate-300">OpenAI / Anthropic</span> — Traitement IA des CV
                (données transmises sans conservation permanente)
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">5. Durée de conservation</h2>
            <ul className="list-disc list-inside space-y-1 text-slate-400">
              <li>Données des utilisateurs : durée de la relation contractuelle + 3 ans</li>
              <li>
                Données des candidats : jusqu'à suppression par l'utilisateur ou 2 ans après le
                dernier accès
              </li>
              <li>Logs de sécurité : 12 mois</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">6. Vos droits</h2>
            <p>Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-slate-400">
              <li>
                <span className="text-slate-300">Droit d'accès</span> — obtenir une copie de vos
                données
              </li>
              <li>
                <span className="text-slate-300">Droit de rectification</span> — corriger des
                données inexactes
              </li>
              <li>
                <span className="text-slate-300">Droit à l'effacement</span> — demander la
                suppression de vos données
              </li>
              <li>
                <span className="text-slate-300">Droit à la limitation</span> — restreindre le
                traitement de vos données
              </li>
              <li>
                <span className="text-slate-300">Droit à la portabilité</span> — recevoir vos
                données dans un format structuré
              </li>
              <li>
                <span className="text-slate-300">Droit d'opposition</span> — vous opposer à certains
                traitements
              </li>
            </ul>
            <p className="mt-3">
              Pour exercer vos droits, contactez-nous à{' '}
              <a href="mailto:contact@revertai.io" className="text-amber-500 hover:underline">
                contact@revertai.io
              </a>
              . Nous répondons dans un délai d'un mois.
            </p>
            <p className="mt-2">
              Vous disposez également du droit de déposer une réclamation auprès de la CNIL (
              <a
                href="https://www.cnil.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-500 hover:underline"
              >
                www.cnil.fr
              </a>
              ).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">7. Sécurité</h2>
            <p>
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour
              protéger vos données contre tout accès non autorisé, perte ou divulgation :
              chiffrement en transit (HTTPS), contrôle d'accès par rôle, authentification sécurisée.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">8. Cookies</h2>
            <p>
              Nous utilisons uniquement des cookies strictement nécessaires au fonctionnement du
              service (session d'authentification). Aucun cookie publicitaire ou de tracking tiers
              n'est utilisé.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">9. Modifications</h2>
            <p>
              Cette politique peut être mise à jour. En cas de modification substantielle, les
              utilisateurs en seront informés par e-mail ou notification sur la plateforme.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">10. Contact</h2>
            <p>
              Pour toute question relative à la protection de vos données :{' '}
              <a href="mailto:contact@revertai.io" className="text-amber-500 hover:underline">
                contact@revertai.io
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-6 px-6 mt-12">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <p>© 2025 RevertAI. Tous droits réservés.</p>
          <div className="flex gap-6">
            <Link to="/cgu" className="hover:text-slate-300 transition-colors">
              CGU
            </Link>
            <a href="mailto:contact@revertai.io" className="hover:text-slate-300 transition-colors">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
